import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AppContext';
import { 
  Zap, Clock, ChefHat, Package, Users, Loader2, 
  RefreshCw, BookOpen, AlertTriangle, Flame, Utensils, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const ExpressRecipesWidget = ({ onSaveRecipe }) => {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('express'); // 'express' or 'mise_en_place'
  const [randomRecipe, setRandomRecipe] = useState(null);
  const [loadingRandom, setLoadingRandom] = useState(false);
  const [previewRecipe, setPreviewRecipe] = useState(null);
  const [savingRecipe, setSavingRecipe] = useState(null);

  useEffect(() => {
    fetchRecipes();
  }, [activeTab]);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/recipes/express?category=${activeTab}`);
      setRecipes(response.data.recipes);
    } catch (error) {
      console.error('Error fetching express recipes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRandomRecipe = async () => {
    setLoadingRandom(true);
    try {
      const response = await api.get('/recipes/express/random');
      setRandomRecipe(response.data.recipe);
    } catch (error) {
      toast.error('Erreur lors de la récupération');
    } finally {
      setLoadingRandom(false);
    }
  };

  const handleSaveRecipe = async (recipe) => {
    setSavingRecipe(recipe.title);
    try {
      await api.post('/recipes', {
        title: recipe.title,
        description: recipe.description,
        ingredients: recipe.ingredients,
        instructions: recipe.instructions,
        prep_time: recipe.prep_time,
        cook_time: recipe.cook_time,
        servings: String(recipe.servings),
        protein_g: recipe.estimated_protein_g,
        calories: recipe.estimated_calories,
        category: recipe.category,
        tags: recipe.tags
      });
      toast.success('Recette ajoutée à ton carnet!');
      setPreviewRecipe(null);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingRecipe(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="express-recipes-widget">
      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-muted rounded-lg">
        <button
          onClick={() => setActiveTab('express')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'express' 
              ? 'bg-primary text-primary-foreground' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-express"
        >
          <Zap className="w-4 h-4" />
          Express
        </button>
        <button
          onClick={() => setActiveTab('mise_en_place')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'mise_en_place' 
              ? 'bg-primary text-primary-foreground' 
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-mise-en-place"
        >
          <Package className="w-4 h-4" />
          Mise en Place
        </button>
      </div>

      {/* Random Recipe Generator */}
      {activeTab === 'express' && (
        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm uppercase tracking-wide text-amber-400 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Idée Express Aléatoire
            </h3>
            <Button
              size="sm"
              variant="outline"
              onClick={getRandomRecipe}
              disabled={loadingRandom}
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              data-testid="random-recipe-btn"
            >
              {loadingRandom ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Générer
                </>
              )}
            </Button>
          </div>
          
          {randomRecipe && (
            <div className="bg-background/50 rounded-lg p-3">
              <h4 className="font-medium text-foreground">{randomRecipe.title}</h4>
              <p className="text-sm text-muted-foreground mt-1">{randomRecipe.description}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {randomRecipe.total_time}
                </span>
                <span className="flex items-center gap-1 text-primary">
                  {randomRecipe.estimated_protein_g}g protéines
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => handleSaveRecipe(randomRecipe)}
                className="mt-2 bg-amber-500 hover:bg-amber-600"
              >
                <BookOpen className="w-4 h-4 mr-1" />
                Sauvegarder
              </Button>
            </div>
          )}
        </div>
      )}

      {/* User Allergies Warning */}
      {user?.allergies?.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>Les recettes sont filtrées selon vos allergies: {user.allergies.join(', ')}</span>
        </div>
      )}

      {/* Recipes Grid */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recipes.map((recipe, index) => (
            <div 
              key={index}
              className="bg-card border border-border rounded-xl p-4 hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => setPreviewRecipe(recipe)}
              data-testid={`recipe-card-${index}`}
            >
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-foreground">{recipe.title}</h4>
                {recipe.category === 'express' ? (
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                    <Zap className="w-3 h-3 inline mr-1" />
                    Express
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                    <Package className="w-3 h-3 inline mr-1" />
                    Prep
                  </span>
                )}
              </div>
              
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {recipe.description}
              </p>
              
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-3">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {recipe.total_time}
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" /> {recipe.servings} {typeof recipe.servings === 'number' ? 'portions' : ''}
                </span>
                <span className="flex items-center gap-1 text-primary font-medium">
                  {recipe.estimated_protein_g}g prot.
                </span>
                <span className="flex items-center gap-1">
                  {recipe.estimated_calories} cal
                </span>
              </div>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-1 mb-3">
                {recipe.tags?.slice(0, 4).map((tag, i) => (
                  <span 
                    key={i}
                    className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleSaveRecipe(recipe)}
                className="w-full"
              >
                <BookOpen className="w-4 h-4 mr-1" />
                Ajouter au carnet
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Info about meal prep */}
      {activeTab === 'mise_en_place' && (
        <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 text-center">
          <ChefHat className="w-8 h-8 text-purple-400 mx-auto mb-2" />
          <h4 className="font-medium text-purple-300 mb-1">Planifie ta semaine!</h4>
          <p className="text-sm text-muted-foreground">
            Prépare ces recettes le dimanche pour des repas express toute la semaine.
          </p>
        </div>
      )}

      {/* Recipe Preview Dialog */}
      <Dialog open={!!previewRecipe} onOpenChange={() => setPreviewRecipe(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {previewRecipe && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-primary flex items-center gap-2">
                  {previewRecipe.category === 'express' ? (
                    <Zap className="w-6 h-6 text-amber-400" />
                  ) : (
                    <Package className="w-6 h-6 text-purple-400" />
                  )}
                  {previewRecipe.title}
                </DialogTitle>
                <DialogDescription>{previewRecipe.description}</DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Macros */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-primary/10 p-3 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Protéines</p>
                    <p className="font-heading text-xl text-primary">{previewRecipe.estimated_protein_g}g</p>
                  </div>
                  <div className="bg-secondary p-3 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Calories</p>
                    <p className="font-heading text-xl">{previewRecipe.estimated_calories}</p>
                  </div>
                  <div className="bg-secondary p-3 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Préparation</p>
                    <p className="font-heading text-lg">{previewRecipe.prep_time}</p>
                  </div>
                  <div className="bg-secondary p-3 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground">Portions</p>
                    <p className="font-heading text-lg">{previewRecipe.servings}</p>
                  </div>
                </div>

                {/* Time info */}
                <div className="flex flex-wrap gap-3">
                  {previewRecipe.prep_time && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      Préparation: {previewRecipe.prep_time}
                    </div>
                  )}
                  {previewRecipe.cook_time && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Flame className="w-4 h-4" />
                      Cuisson: {previewRecipe.cook_time}
                    </div>
                  )}
                  {previewRecipe.total_time && (
                    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                      Total: {previewRecipe.total_time}
                    </div>
                  )}
                </div>

                {/* Difficulty & Storage */}
                <div className="flex flex-wrap gap-2">
                  {previewRecipe.difficulty && (
                    <span className="px-3 py-1 bg-muted text-muted-foreground text-sm rounded-full">
                      Difficulté: {previewRecipe.difficulty}
                    </span>
                  )}
                  {previewRecipe.storage_days && (
                    <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-sm rounded-full">
                      Conservation: {previewRecipe.storage_days} jours
                    </span>
                  )}
                </div>

                {/* Tags */}
                {previewRecipe.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {previewRecipe.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Ingredients */}
                {previewRecipe.ingredients?.length > 0 && (
                  <div>
                    <h4 className="font-heading uppercase text-sm text-primary mb-2 flex items-center gap-2">
                      <Utensils className="w-4 h-4" />
                      Ingrédients
                    </h4>
                    <ul className="space-y-1 bg-secondary/30 rounded-lg p-3">
                      {previewRecipe.ingredients.map((ing, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-primary">•</span>
                          <span>{ing}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Instructions */}
                {previewRecipe.instructions?.length > 0 && (
                  <div>
                    <h4 className="font-heading uppercase text-sm text-primary mb-2">
                      Instructions
                    </h4>
                    <ol className="space-y-3">
                      {previewRecipe.instructions.map((step, i) => (
                        <li key={i} className="flex gap-3 text-sm">
                          <span className="flex-shrink-0 w-6 h-6 bg-primary/20 text-primary rounded-full flex items-center justify-center text-xs font-bold">
                            {i + 1}
                          </span>
                          <span className="text-muted-foreground pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Add to Book Button */}
                <div className="flex gap-3 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    onClick={() => setPreviewRecipe(null)}
                    className="flex-1"
                  >
                    Fermer
                  </Button>
                  <Button
                    onClick={() => handleSaveRecipe(previewRecipe)}
                    disabled={savingRecipe === previewRecipe.title}
                    className="flex-1 bg-primary text-primary-foreground"
                  >
                    {savingRecipe === previewRecipe.title ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <BookOpen className="w-4 h-4 mr-2" />
                    )}
                    Ajouter à mon carnet
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpressRecipesWidget;
